import { sanitizeStorageFileName } from '../utils/storageUtils';

/** Browser file-picker hint — images, PDFs, Office, archives, common site media. */
export const WORKSPACE_FILE_ACCEPT = [
  'image/*',
  'video/*',
  'audio/*',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.zip',
  '.rar',
  '.7z',
  '.dwg',
  '.dxf',
  '.heic',
  '.heif',
  '.tif',
  '.tiff',
  '.msg',
  '.eml',
  '.pages',
  '.numbers',
  '.key',
  '.webp',
  '.svg',
].join(',');

/** @deprecated use WORKSPACE_FILE_ACCEPT */
export const RECEIPT_IMAGE_ACCEPT = WORKSPACE_FILE_ACCEPT;

const ALLOWED_MIME_PREFIXES = ['image/', 'video/', 'audio/'];
const ALLOWED_MIME_EXACT = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'application/acad',
  'image/vnd.dwg',
  'application/octet-stream',
]);

const EXT_OK =
  /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|tiff?|pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar|7z|dwg|dxf|msg|eml|pages|numbers|key|mov|mp4|m4v|avi|mkv|webm|mp3|wav|m4a|aac|flac|ogg|rtf|json|xml|log|psd|ai|eps|cad|ifc|ods|odt|odp)$/i;

const BLOCKED_FILE_EXTENSIONS = new Set([
  'exe',
  'bat',
  'cmd',
  'scr',
  'msi',
  'com',
  'pif',
  'dll',
  'vbs',
  'js',
  'jar',
  'app',
]);

function fileExtensionLower(name) {
  const n = name || '';
  const i = n.lastIndexOf('.');
  if (i < 0 || i === n.length - 1) return '';
  return n.slice(i + 1).toLowerCase();
}

/** 50 MB — project vault / photos / receipt attachments */
export const MAX_PROJECT_WORKSPACE_FILE_BYTES = 50 * 1024 * 1024;

/** @deprecated use MAX_PROJECT_WORKSPACE_FILE_BYTES */
export const MAX_RECEIPT_BYTES = MAX_PROJECT_WORKSPACE_FILE_BYTES;

function mimeAllowed(type) {
  const t = (type || '').toLowerCase();
  if (!t) return false;
  if (ALLOWED_MIME_EXACT.has(t)) return true;
  return ALLOWED_MIME_PREFIXES.some((p) => t.startsWith(p));
}

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, userMessage: string }}
 */
export function validateProjectWorkspaceFile(file) {
  if (!file) {
    return { ok: false, userMessage: 'Choose a file to upload.' };
  }
  const type = (file.type || '').toLowerCase();
  const name = file.name || '';
  if (BLOCKED_FILE_EXTENSIONS.has(fileExtensionLower(name))) {
    return { ok: false, userMessage: 'That file type cannot be uploaded for security reasons.' };
  }
  if (file.size > MAX_PROJECT_WORKSPACE_FILE_BYTES) {
    return { ok: false, userMessage: 'That file is too large. Maximum size is 50 MB.' };
  }
  if (file.size < 1) {
    return { ok: false, userMessage: 'That file looks empty.' };
  }
  const extOk = EXT_OK.test(name);
  if (mimeAllowed(type) || extOk) {
    return { ok: true };
  }
  // Allow other job-site files with a normal extension (e.g. proprietary CAD, scans, etc.)
  if (/\.\w{1,20}$/i.test(name.trim())) {
    return { ok: true };
  }
  return {
    ok: false,
    userMessage:
      'Could not accept that file. Rename it to include an extension (e.g. .pdf, .jpg) or use a common document or image format.',
  };
}

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, userMessage: string }}
 */
export function validateReceiptImageFile(file) {
  return validateProjectWorkspaceFile(file);
}

/**
 * Upload a receipt image to the shared project-files bucket (same pattern as photos/vault).
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ userId: string; projectId: string; file: File }} params
 * @returns {Promise<{ url: string; storagePath: string; kind: 'storage'; uploadedAt: string }>}
 */
export async function uploadExpenseReceipt(supabase, { userId, projectId, file }) {
  const v = validateProjectWorkspaceFile(file);
  if (!v.ok) {
    const e = new Error(v.userMessage);
    e.userFacing = v.userMessage;
    throw e;
  }
  if (!userId || !projectId) {
    const e = new Error('Missing project or session.');
    e.userFacing = 'You need to be signed in with a project open to upload receipts.';
    throw e;
  }

  const safeName = sanitizeStorageFileName(file.name);
  const path = `users/${userId}/projects/${projectId}/expenses/receipts/${Date.now()}_${safeName}`;

  const contentType =
    file.type && file.type.trim()
      ? file.type
      : /\.pdf$/i.test(safeName)
        ? 'application/pdf'
        : 'application/octet-stream';

  const { error } = await supabase.storage.from('project-files').upload(path, file, {
    upsert: true,
    contentType,
  });
  if (error) {
    const err = new Error(error.message || 'Upload failed');
    err.userFacing = 'We could not upload that receipt. Check your connection and try again.';
    throw err;
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from('project-files').getPublicUrl(path);

  return {
    url: publicUrl,
    storagePath: path,
    kind: 'storage',
    uploadedAt: new Date().toISOString(),
  };
}
